import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { VehicleTripHistory } from '@/pages/TripsPage';
import {
  ArrowLeft,
  Truck,
  Gauge,
  MapPin,
  Fuel,
  Wrench,
  Calendar,
  FileText,
  Route,
  DollarSign,
  Loader2,
  Info,
} from 'lucide-react';

interface VehicleDetail {
  id: string;
  name: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  licensePlate: string;
  color: string;
  status: 'active' | 'maintenance' | 'retired' | 'sold';
  assignedDriver: string | null;
  insuranceExpiry: string | null;
  fuelType: string;
  odometer: number;
  fuelLevel: number | null;
  lastLocation: string | null;
  nextService: string | null;
  purchaseDate: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  maintenance: 'bg-red-500/10 text-red-400 border-red-500/30',
  retired: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30',
  sold: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench },
  { id: 'trips', label: 'Trips', icon: Route },
  { id: 'costs', label: 'Costs', icon: DollarSign },
  { id: 'documents', label: 'Documents', icon: FileText },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function VehicleDetailPage() {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useApi<VehicleDetail>(vehicleId ? `/api/vehicles/${vehicleId}` : null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/vehicles')} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Vehicles
        </button>
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-12 text-center">
          <Truck className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
          <p className="text-zinc-500">Vehicle not found</p>
        </div>
      </div>
    );
  }

  const v = data;

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <button onClick={() => navigate('/vehicles')} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Vehicles
      </button>

      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-emerald-500/10">
          <Truck className="w-6 h-6 text-emerald-500" />
        </div>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-zinc-100">{v.name}</h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${STATUS_STYLES[v.status] ?? STATUS_STYLES.retired}`}>
              {v.status}
            </span>
          </div>
          <p className="text-sm text-zinc-400">{v.year} {v.make} {v.model}</p>
        </div>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vehicle Info */}
        <div className="lg:col-span-2 bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide mb-4">Vehicle Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label="Make" value={v.make} />
            <InfoRow label="Model" value={v.model} />
            <InfoRow label="Year" value={String(v.year)} />
            <InfoRow label="VIN" value={v.vin} mono />
            <InfoRow label="License Plate" value={v.licensePlate} mono />
            <InfoRow label="Color" value={v.color} />
            <InfoRow label="Assigned Driver" value={v.assignedDriver ?? 'Unassigned'} />
            <InfoRow label="Insurance Expiry" value={v.insuranceExpiry ?? '—'} />
            <InfoRow label="Fuel Type" value={v.fuelType} />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Odometer */}
          <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">Odometer</span>
            </div>
            <span className="text-3xl font-bold text-zinc-100">{v.odometer.toLocaleString()}</span>
            <span className="text-sm text-zinc-500 ml-1">miles</span>
          </div>

          {/* Mini Map Placeholder */}
          <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-emerald-500" />
              <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">Location</span>
            </div>
            <div className="h-32 rounded-lg border border-zinc-700 bg-zinc-800/50 flex items-center justify-center">
              <span className="text-xs text-zinc-500">Map preview unavailable</span>
            </div>
            {v.lastLocation && (
              <p className="text-xs text-zinc-400 mt-2">{v.lastLocation}</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-800">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <OverviewCard icon={MapPin} label="Last Location" value={v.lastLocation ?? 'Unknown'} />
            <OverviewCard icon={Fuel} label="Fuel Level" value={v.fuelLevel != null ? `${v.fuelLevel}%` : '—'} />
            <OverviewCard icon={Wrench} label="Next Service" value={v.nextService ?? 'Not scheduled'} />
            <OverviewCard icon={Calendar} label="Purchase Date" value={v.purchaseDate ?? '—'} />
          </div>
        )}
        {activeTab === 'trips' && (
          <VehicleTripHistory vehicleId={vehicleId!} />
        )}
        {(activeTab === 'maintenance' || activeTab === 'costs' || activeTab === 'documents') && (
          <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-12 text-center">
            <FileText className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
            <p className="text-zinc-500">No {activeTab} records yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-zinc-500 mb-0.5">{label}</dt>
      <dd className={`text-sm text-zinc-200 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}

function OverviewCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-emerald-500" />
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">{label}</span>
      </div>
      <span className="text-sm font-medium text-zinc-200">{value}</span>
    </div>
  );
}
